import {z} from 'zod';

import {
  BaseVoidResponseSchema,
  PaymentTypeSchema,
  HeaderDynamicActionsFlagsSchema,
  PriceWithAndWithoutVatSchema,
  CreatedAtAndUpdatedBySchema,
} from '@dms/api/shared';

export const DuplicateDeliveryNoteSchema = z.object({
  deliveryNoteId: z.string(),
  supplierId: z.string(),
  warehouseId: z.string().nullish(),
  authorizationProfileId: z.string(),
  state: z.enum(['PENDING', 'RECEIVED', 'COMPLETED']),
  deliveryNoteNumber: z.string().nullish(),
  createdAt: z.string(),
  createdBy: z.string(),
  invoiceNumber: z.string().nullish(),
  invoiceIssueDate: z.string().nullish(),
});
export type DuplicateDeliveryNote = z.infer<typeof DuplicateDeliveryNoteSchema>;

export const CatalogNumberSuggestionSchema = z.object({
  catalogueNumber: z.string(),
  articles: z.array(
    z.object({
      warehouseArticleId: z.string().nullish(),
      name: z.string(),
      measurementUnitId: z.string().nullish(),
      vatType: z.string().nullish(),
      manufacturerId: z.string().nullish(),
      warehouseId: z.string().nullish(),
      manufacturer: z.object({
        manufacturerId: z.string(),
        name: z.string(),
      }),
    })
  ),
});
export type CatalogNumberSuggestion = z.infer<typeof CatalogNumberSuggestionSchema>;

/**
 * GET requests
 */
export const GetDeliveryNoteDuplicatesRequestSchema = z.object({
  supplierId: z.string(),
  invoiceNumber: z.string(),
  deliveryNoteNumber: z.string(),
});
export type GetDeliveryNoteDuplicatesRequest = z.infer<
  typeof GetDeliveryNoteDuplicatesRequestSchema
>;

export const GetDeliveryNoteDuplicatesResponseSchema = z.array(DuplicateDeliveryNoteSchema);
export type GetDeliveryNoteDuplicatesResponse = z.infer<
  typeof GetDeliveryNoteDuplicatesResponseSchema
>;

export const GetDeliveryNoteRequestSchema = z.object({
  deliveryNoteId: z.string(),
});
export type GetDeliveryNoteRequest = z.infer<typeof GetDeliveryNoteRequestSchema>;

const DeliveryNoteStateSchema = z.enum(['PENDING', 'RECEIVED', 'COMPLETED']);

export const GetDeliveryNoteResponseSchema = z
  .object({
    deliveryNoteId: z.string(),
    supplierId: z.string(),
    warehouseId: z.string().optional(),
    authorizationProfileId: z.string(),
    state: DeliveryNoteStateSchema,
    type: z.enum(['EXTERNAL_TRANSFER', 'OPENING_BALANCE', 'PURCHASE', 'TRANSFER']),
    transferSourceBranchId: z.string().optional(),
    branchId: z.string().optional(),
    deliveryNoteNumber: z.string().optional(),
    invoiceNumber: z.string().optional(),
    paymentType: PaymentTypeSchema.optional(),
    currency: z.string().optional(),
    exchangeRate: z.number().optional(),
    referenceNumber: z.string().optional(),
    ncConversion: z.string().optional(),
    issuedAt: z.string().optional(),
    isFromReceiveNote: z.boolean(),
    totalPrice: PriceWithAndWithoutVatSchema,
    note: z.string().optional(),
    invoiceIssueDate: z.string().optional(),
    receiveNote: z.object({
      receiveNoteId: z.string().optional(),
      warehouseId: z.string().optional(),
      deliveryNoteId: z.string().optional(),
      supplierId: z.string().optional(),
      receiveNoteNumber: z.string().optional(),
      state: z.enum(['PENDING', 'COMPLETED']),
      paymentType: z.string().optional(),
      currency: z.string().optional(),
      exchangeRate: z.number().optional(),
      ncConversion: z.string().optional(),
      note: z.string().optional(),
      createdAt: z.string().optional(),
      createdBy: z.string().optional(),
      updatedAt: z.string().optional(),
      updatedBy: z.string().optional(),
      authorizationProfileId: z.string().optional(),
      invoiceNumber: z.string().optional(),
      invoiceDate: z.string().optional(),
      issuedAt: z.string().optional(),
      isFromReceiveNote: z.boolean().optional(),
      invoiceIssuedAt: z.string().optional(),
      invoiceIssueDate: z.string().optional(),
    }),
    markers: HeaderDynamicActionsFlagsSchema,
  })
  .merge(CreatedAtAndUpdatedBySchema);
export type GetDeliveryNoteResponse = z.infer<typeof GetDeliveryNoteResponseSchema>;

export const GetDeliveryNoteManualDownloadRequestSchema = z.object({
  params: z.object({dateFrom: z.string()}),
});
export type GetDeliveryNoteManualDownloadRequest = z.infer<
  typeof GetDeliveryNoteManualDownloadRequestSchema
>;

export const GetDeliveryNoteManualDownloadResponseSchema = BaseVoidResponseSchema;
export type GetDeliveryNoteManualDownloadResponse = z.infer<
  typeof GetDeliveryNoteManualDownloadResponseSchema
>;

/**
 * POST requests
 */
export const PostDeliveryNoteRequestSchema = z.object({
  supplierId: z.string(),
  warehouseId: z.string(),
  deliveryNoteNumber: z.string().nullish(),
  invoiceNumber: z.string().nullish(),
  authorizationProfileId: z.string(),
  paymentType: PaymentTypeSchema,
  currency: z.string(),
  item: z.array(
    z.object({
      catalogNumber: z.string(),
      name: z.string(),
      vatType: z.string(),
      vatRate: z.number(),
      purchasePrice: z.number(),
      stockValue: z.number(),
      quantity: z.number(),
      manufacturerId: z.string(),
      handlingUnit: z.string(),
    })
  ),
});
export type PostDeliveryNoteRequest = z.infer<typeof PostDeliveryNoteRequestSchema>;

export const PostDeliveryNoteResponseSchema = z.object({
  deliveryNoteId: z.string(),
});
export type PostDeliveryNoteResponse = z.infer<typeof PostDeliveryNoteResponseSchema>;

export const BulkPostReceiveNoteRequestSchema = z.object({
  deliveryNoteId: z.array(z.string()),
});
export type BulkPostReceiveNoteRequest = z.infer<typeof BulkPostReceiveNoteRequestSchema>;

export const BulkPostReceiveNoteResponseSchema = BaseVoidResponseSchema;
export type BulkPostReceiveNoteResponse = z.infer<typeof BulkPostReceiveNoteResponseSchema>;

export const BulkPostCatalogNumberSuggestionsRequestSchema = z.object({
  supplierId: z.string(),
  searchForOtherSuppliers: z.boolean(),
  warehouseId: z.string(),
  searchInOtherWarehouses: z.boolean(),
  catalogueNumbers: z.array(z.string()),
});
export type BulkPostCatalogNumberSuggestionsRequest = z.infer<
  typeof BulkPostCatalogNumberSuggestionsRequestSchema
>;

export const BulkPostCatalogNumberSuggestionsResponseSchema = z.array(
  CatalogNumberSuggestionSchema
);
export type BulkPostCatalogNumberSuggestionsResponse = z.infer<
  typeof BulkPostCatalogNumberSuggestionsResponseSchema
>;

/**
 * PATCH requests
 */
const PatchDeliveryNoteRequestBodySchema = z.object({
  warehouseId: z.string(),
  supplierId: z.string().nullish(),
  authorizationProfileId: z.string().nullish(),
  deliveryNoteNumber: z.string().nullish(),
  issuedAt: z.string().nullish(),
  invoiceNumber: z.string().nullish(),
  paymentType: PaymentTypeSchema,
  currency: z.string().nullish(),
  exchangeRate: z.number().nullish(),
  ncConversion: z.string().nullish(),
  note: z.string().nullish(),
});

export const PatchDeliveryNoteRequestSchema = z.object({
  deliveryNoteId: z.string(),
  body: PatchDeliveryNoteRequestBodySchema,
});
export type PatchDeliveryNoteRequest = z.infer<typeof PatchDeliveryNoteRequestSchema>;

export const PatchDeliveryNoteResponseSchema = BaseVoidResponseSchema;
export type PatchDeliveryNoteResponse = z.infer<typeof PatchDeliveryNoteResponseSchema>;

const PatchDeliveryNoteForCompletedRequestBodySchema = z.object({
  invoiceNumber: z.string().nullish(),
  invoiceIssueDate: z.string().nullish(),
  deliveryNoteNumber: z.string().nullish(),
  issuedAt: z.string().nullish(),
  note: z.string().nullish(),
});

export const PatchDeliveryNoteForCompletedRequestSchema = z.object({
  deliveryNoteId: z.string(),
  body: PatchDeliveryNoteForCompletedRequestBodySchema,
});
export type PatchDeliveryNoteForCompletedRequest = z.infer<
  typeof PatchDeliveryNoteForCompletedRequestSchema
>;

export const PatchDeliveryNoteForCompletedResponseSchema = BaseVoidResponseSchema;
export type PatchDeliveryNoteForCompletedResponse = z.infer<
  typeof PatchDeliveryNoteForCompletedResponseSchema
>;

export const BulkPatchDeliveryNoteRequestSchema = z.object({
  deliveryNoteId: z.array(z.string()),
  warehouseId: z.string().nullish(),
  currency: z.string().nullish(),
  invoiceNumber: z.string().nullish(),
  invoiceIssueDate: z.string().nullish(),
});
export type BulkPatchDeliveryNoteRequest = z.infer<typeof BulkPatchDeliveryNoteRequestSchema>;

export const BulkPatchDeliveryNoteResponseSchema = BaseVoidResponseSchema;
export type BulkPatchDeliveryNoteResponse = z.infer<typeof BulkPatchDeliveryNoteResponseSchema>;

export const BulkEditInvoiceDeliveryNoteRequestSchema = z.object({
  deliveryNoteIds: z.array(z.string()),
  invoiceNumber: z.string().nullish(),
  invoiceIssueDate: z.string().nullish(),
});
export type BulkEditInvoiceDeliveryNoteRequest = z.infer<
  typeof BulkEditInvoiceDeliveryNoteRequestSchema
>;

export const BulkEditInvoiceDeliveryNoteResponseSchema = BaseVoidResponseSchema;
export type BulkEditInvoiceDeliveryNoteResponse = z.infer<
  typeof BulkEditInvoiceDeliveryNoteResponseSchema
>;

/**
 * DELETE requests
 */
export const BulkDeleteDeliveryNoteRequestSchema = z.object({
  deliveryNoteId: z.array(z.string()),
});
export type BulkDeleteDeliveryNoteRequest = z.infer<typeof BulkDeleteDeliveryNoteRequestSchema>;

export const BulkDeleteDeliveryNoteResponseSchema = BaseVoidResponseSchema;
export type BulkDeleteDeliveryNoteResponse = z.infer<typeof BulkDeleteDeliveryNoteResponseSchema>;
